import { ReleaseTymberAdvancesData } from '@tymbe/schema/release-tymber-advances.interface';
import moment from 'moment-timezone';
import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, NavLink, Outlet } from 'react-router-dom';
import AccountState from '../../../../components/AccountState/AccountState';
import AttendanceRate from './AttendanceRate';
import Blocks from './Blocks';
import RemoveRegistrationButton from './RemoveRegistrationButton';
import StarRating from './StarRating';
import feathersClient from '../../../../apiClient';
import Protect from '../../../../apiClient/Protect';
import { PrimaryButton } from '../../../../components/buttons';
import PhoneVerificationBadge from '../../../../components/PhoneVerificationBadge/PhoneVerificationBadge';

import Card from '../../../../components/Layout/Card';
import NavTabs from '../../../../components/navTabs/NavTabs';
import ReleaseTymberAdvancesModal from '../../../../components/ReleaseTymberAdvances/ReleaseTymberAdvancesModal';
import { Roles } from '../../../../utils/enums';
import getGender from '../../../../utils/gender';
import { getLatestPhone } from '../../../../utils/phoneNumber';
import Spinner from '../../../../utils/spinner';
import { getAge } from '../../../../utils/time';
import { TyLivesModal } from '../lives/TyLivesModal';
import { EditIcon } from '@tymbe/ty-components/icons';
import { TyLifeScore } from '../lives/TyLivesScore';
import { useLifeTransactions } from './useLifeTransactions';
import { FavouriteIcon } from '@tymbe/ui-icons';
import ChangeIkMpsvOic from './ChangeIkMpsvOic';
import { useUser } from '../../../../apiClient/ApiContext';

interface UserDetailProps {
  personId: string;
}

const UserDetailContainer = ({ personId }: UserDetailProps) => {
  const user = useUser();
  const [isTyLivesModalOpen, setIsTyLivesModalOpen] = useState<boolean>(false)
  const toggleTyLivesModal = () => setIsTyLivesModalOpen(prev => !prev);

  const [showReleaseAdvancesModal, setShowReleaseAdvancesModal] = useState(false);
  const [releaseAdvancesResult, setReleaseAdvancesResult] = useState<ReleaseTymberAdvancesData & { error?: string }>();

  const queryClient = useQueryClient();

  const { data: person, isLoading: personLoading } = useQuery(['person', personId], async () =>
    feathersClient
      .service('person')
      .get(personId, { query: { $eager: '[accountState, blockedCompany, contact]' } }));

  const { data: personData, isLoading: personDataLoading, refetch } = useQuery(
    ['personData', personId],
    async () => feathersClient
      .service('person-data').get(personId, { query: { $modify: 'attendanceRate' } }),
    { retry: false },
  );

  const { data: companyBlockedPerson } = useQuery(['company-blocked-user', personId], async () =>
    feathersClient
      .service('company-blocked-user')
      .find({
        query: {
          person_id: personId,
          $eager: '[company, branchoffice]',
          $sort: {
            created_at: -1,
          },
        },
      }));

  const changeMpsvIkOic = useMutation({
    mutationKey: 'changeMpsvIkOic',
    mutationFn: async ({ personId, mpsvIkOic }: { personId: number, mpsvIkOic: string }) => feathersClient.service('person-data').patch(personId, { mpsv_oic: mpsvIkOic }),
    onSuccess: (_, props) => {
      if (personData) personData.mpsv_oic = props.mpsvIkOic;
      queryClient.invalidateQueries(['personData', personId]);
    }
});

  const { mutateAsync: releaseAdvances, isLoading: advancesAreBeingReleased } = useMutation(
    ['releaseTymberAdvances', personId],
    async (data: ReleaseTymberAdvancesData) => feathersClient.service('release-tymber-advances').create(data),
  );

  const onReleaseAdvancesClick = () => {
    setShowReleaseAdvancesModal(!advancesAreBeingReleased);
  };

  const onReleaseAdvancesModalSubmit = async () => {
    if (advancesAreBeingReleased) {
      return;
    }

    try {
      const startOfPreviousPayInterval = moment.tz().startOf('month').subtract(1, 'month');

      const result = await releaseAdvances({
        tymberId: Number(personId),
        year: startOfPreviousPayInterval.year(),
        month: startOfPreviousPayInterval.month() + 1,
      });
      setReleaseAdvancesResult(result);
    } catch {
      const result = {
        tymberId: 0,
        year: 0,
        month: 0,
        error: 'Při uvolňování záloh došlo k chybě.',
      };
      setReleaseAdvancesResult(result);
    }
  };

  const onReleaseAdvancesModalClose = () => {
    setShowReleaseAdvancesModal(false);
    setReleaseAdvancesResult(undefined);
  };

  const lifeTransactionQuery = useLifeTransactions(personId);

  useEffect(() => {
    refetch()
  }, [lifeTransactionQuery.data])


  const latestPhone = getLatestPhone(person?.contact);

  if (personDataLoading || personLoading) return <Spinner />;

  return (
    <div className="userInfo flex flex-col gap-5 [overflow-anchor:none]">
      <div className="ty-user-detail-name flex gap-5 px-6 pt-10 items-center">
        {person?.first_name} {person?.last_name}
        {personData && (
          <Protect auth={[Roles.SUPER_ADMIN, Roles.TYMBE_ADMIN, Roles.ADMIN, Roles.TYMBE_COORDINATOR]} redirect={false}>
            <Link to={`/user/${personId}/edit`}>
              <PrimaryButton
                className="ty-icon-button ty-icon-button-toggle bg-primary"
                type="link"
                icon={<EditIcon />}
              />
            </Link>
          </Protect>
        )}
        <div className="grow" />
        <Protect auth={[Roles.SUPER_ADMIN, Roles.TYMBE_ADMIN]} redirect={false}>
          <ReleaseTymberAdvancesModal
            advancesAreBeingReleased={advancesAreBeingReleased}
            onClose={onReleaseAdvancesModalClose}
            onSubmit={onReleaseAdvancesModalSubmit}
            releaseAdvancesResult={releaseAdvancesResult}
            showModal={showReleaseAdvancesModal}
            onReleaseAdvancesClick={onReleaseAdvancesClick}
          />
        </Protect>
        <Protect auth={[Roles.SUPER_ADMIN, Roles.TYMBE_ADMIN, Roles.TYMBE_COORDINATOR]} redirect={false}>
          <RemoveRegistrationButton personId={Number(personId)} />
        </Protect>
      </div>

      <div className="flex gap-3 text-left px-6 items-center">
        <div className="ty-h4">
          {personData?.gender ? getGender(personData.gender) : 'Žádná data '}:
          {personData?.birthdate ? ` ${getAge(personData.birthdate)} let` : ' Žádná data'}
        </div>
        <div className="ty-h4">ID: {personId}</div>
        <Protect
          auth={[Roles.SUPER_ADMIN, Roles.TYMBE_ADMIN, Roles.ADMIN, Roles.TYMBE_COORDINATOR, Roles.COMPANY]}
          redirect={false}
        >
          <div className="ty-h4 flex gap-2 items-center">
            MPSV IK/OIC:{' '}
            {personData?.mpsv_oic ?? (
              <ChangeIkMpsvOic
                onSubmit={async (data) =>
                  await changeMpsvIkOic.mutateAsync({ personId: Number(personId), mpsvIkOic: data.mpsv_oic })
                }
              >
                Zadat přidělené IK MPSV / OIČ
              </ChangeIkMpsvOic>
            )}
          </div>
        </Protect>
        <AccountState accountState={person?.accountState} />
        <Protect auth={[Roles.SUPER_ADMIN, Roles.TYMBE_ADMIN, Roles.TYMBE_COORDINATOR, Roles.ADMIN]} redirect={false}>
          <PhoneVerificationBadge contact={latestPhone} />
        </Protect>
      </div>
      <Card>
        <h3 className="ty-h3 mb-6">Hodnocení</h3>
        <div className="md:flex justify-start gap-16 items-end">
          <div>
            <div className="flex justify-start items-center h-35 mb-10">
              <TyLifeScore lives={personData?.lives ?? 0} />
              <Protect auth={[Roles.SUPER_ADMIN, Roles.TYMBE_ADMIN]} redirect={false}>
                <div onClick={toggleTyLivesModal} className="cursor-pointer ml-2">
                  <FavouriteIcon name="mode_edit" className="size-5" />
                </div>
              </Protect>
            </div>
            <div className="ty-faded-small pt-5">Počet životů</div>
          </div>
          <div>
            <div className="flex justify-start items-center h-35 mb-10">
              <StarRating ratingValue={personData?.job_evaluation} />
            </div>
            <div className="ty-faded-small pt-5">Hodnocení práce</div>
          </div>
          <div>
            <div className="ty-primary-h3 flex justify-start items-center h-35 mb-10">
              <AttendanceRate personDetail={personData} />
            </div>
            <div className="ty-faded-small">Docházkovost</div>
          </div>
          <Protect auth={[Roles.SUPER_ADMIN, Roles.TYMBE_ADMIN, Roles.ADMIN, Roles.TYMBE_COORDINATOR]} redirect={false}>
            <div>
              <div className="ty-primary-h3 flex justify-start items-center h-35 mb-10">
                <Blocks companyBlockedUser={companyBlockedPerson?.data} />
              </div>
              <div className="ty-faded-small">Blokace</div>
            </div>
          </Protect>
        </div>
      </Card>
      <Card>
        <div className="relative border-b border-secondary-200 pb-2 pl-3">
          <NavTabs>
            <NavLink
              key="shifts"
              to={`/user/${personId}/shifts`}
              className={({ isActive }) => (isActive ? 'text-secondary font-semibold active' : 'text-secondary-400')}
            >
              Směny
            </NavLink>
            {/* Maybe in the future */}
            {/* <NavLink key="payouts" to={`/user/${personId}/payouts`} className={({ isActive }) => (isActive ? 'text-secondary font-semibold active' : 'text-secondary-400')}>Výplaty</NavLink> */}
            {/* <NavLink key="attendances" to={`/user/${personId}/attendances`} className={({ isActive }) => (isActive ? 'text-secondary font-semibold active' : 'text-secondary-400')}>Potvrzení</NavLink> */}
            {/* <NavLink key="logs" to={`/user/${personId}/logs`} className={({ isActive }) => (isActive ? 'text-secondary font-semibold active' : 'text-secondary-400')}>Log</NavLink> */}
            <Protect
              auth={[Roles.SUPER_ADMIN, Roles.TYMBE_ADMIN, Roles.ADMIN, Roles.TYMBE_COORDINATOR]}
              redirect={false}
              key="admin-only"
            >
              <NavLink
                key="bans"
                to={`/user/${personId}/bans`}
                className={({ isActive }) => (isActive ? 'text-secondary font-semibold active' : 'text-secondary-400')}
              >
                Bany
              </NavLink>
              <NavLink
                key="wallet"
                to={`/user/${personId}/wallet`}
                className={({ isActive }) => (isActive ? 'text-secondary font-semibold active' : 'text-secondary-400')}
                hidden={personData?.is_tymbe_intern && !user.hasRoles([Roles.SUPER_ADMIN])}
              >
                Peněženka
              </NavLink>
              <NavLink
                key="credits"
                to={`/user/${personId}/credits`}
                className={({ isActive }) => (isActive ? 'text-secondary font-semibold active' : 'text-secondary-400')}
              >
                Kredity
              </NavLink>
            </Protect>
            <NavLink
              key="documents"
              to={`/user/${personId}/documents`}
              className={({ isActive }) => (isActive ? 'text-secondary font-semibold active' : 'text-secondary-400')}
            >
              Dokumenty
            </NavLink>
            <NavLink
              key="contacts"
              to={`/user/${personId}/contacts`}
              className={({ isActive }) => (isActive ? 'text-secondary font-semibold active' : 'text-secondary-400')}
            >
              Kontakty
            </NavLink>
            <NavLink
              key="notes"
              to={`/user/${personId}/notes`}
              className={({ isActive }) => (isActive ? 'text-secondary font-semibold active' : 'text-secondary-400')}
            >
              Poznámky
            </NavLink>
            <Protect
              auth={[
                Roles.SUPER_ADMIN,
                Roles.TYMBE_ADMIN,
                Roles.TYMBE_COORDINATOR,
                Roles.COMPANY,
                Roles.BRANCHOFFICE_MANAGER,
              ]}
              redirect={false}
            >
              <NavLink
                key="blocks"
                to={`/user/${personId}/blocks`}
                className={({ isActive }) => (isActive ? 'text-secondary font-semibold active' : 'text-secondary-400')}
              >
                Blokace
              </NavLink>
              <NavLink
                key="utility"
                to={`/user/${personId}/utility`}
                className={({ isActive }) => (isActive ? 'text-secondary font-semibold active' : 'text-secondary-400')}
              >
                Pomůcky
              </NavLink>
            </Protect>
            <Protect auth={[Roles.SUPER_ADMIN, Roles.TYMBE_ADMIN]} redirect={false}>
              <NavLink
                key="lives"
                to={`/user/${personId}/lives`}
                className={({ isActive }) => (isActive ? 'text-secondary font-semibold active' : 'text-secondary-400')}
              >
                Životy
              </NavLink>
            </Protect>
          </NavTabs>
        </div>
      </Card>
      <Outlet />

      {/*
      // Due to absence of some state management system, react context is used to
      // pass the data from lifeTransactionQuery to the TyTimeline component without
      // the need to drill the data through parent components
      */}
      <TyLivesModal isOpen={isTyLivesModalOpen} toggleIsOpen={toggleTyLivesModal} />
    </div>
  );
};

export default UserDetailContainer;
